import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, ShieldCheck, Sparkles } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/api";
import { useM2, fmtM2 } from "@/lib/stats";

const HERO_IMG =
  "https://images.unsplash.com/photo-1657346088167-b982455bf29a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span custom={i} variants={line} initial="hidden" animate="show" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onCta }) {
  const m2 = useM2();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* framed parallax image */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 md:inset-x-auto md:right-0 md:top-0 md:h-full md:w-[52%] overflow-hidden border-l border-white/10">
          <motion.img
            src={HERO_IMG}
            alt="Moderný dom pripravený na zateplenie"
            style={{ y, scale }}
            className="w-full h-full object-cover grayscale-[25%] contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent md:bg-gradient-to-r md:from-ink md:via-ink/60 md:to-transparent" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent md:hidden" />
      </div>

      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-5 md:px-10 pt-28 pb-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs md:text-sm mb-5"
        >
          20 rokov s Vami · od roku 2006 · Slovensko
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05 }}
          className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 text-gold font-mono2 uppercase tracking-[0.2em] text-[10px] md:text-xs px-4 py-2 mb-6 rounded-sm"
          data-testid="hero-first-calc"
        >
          <Sparkles className="w-3.5 h-3.5" /> Prvá online kalkulačka zateplenia na Slovensku
        </motion.div>

        <h1 className="font-display text-white leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
          <Line i={0}>Vypočítajte si</Line>
          <Line i={1} className="text-gold">cenu zateplenia fasády</Line>
          <Line i={2}>do 60 sekúnd</Line>
        </h1>

        <div className="mt-8 inline-flex flex-wrap items-center gap-x-4 gap-y-1 bg-white/5 backdrop-blur-md border border-white/10 border-l-2 border-l-gold px-5 py-3 md:px-6 md:py-4 rounded-sm" data-testid="hero-price">
          <span className="text-white/50 text-xs uppercase tracking-widest font-mono2">Zateplenie fasády už od</span>
          <span className="font-display text-3xl md:text-4xl text-gold leading-none">55 €/m²</span>
          <span className="text-white/40 text-xs">s DPH · na kľúč</span>
        </div>

        <div className="mt-3 flex items-center gap-2 text-white/60 text-xs md:text-sm" data-testid="hero-badge">
          <ShieldCheck className="w-4 h-4 text-gold shrink-0" />
          Cena na kľúč vrátane lešenia · bez skrytých poplatkov
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <button
            onClick={onCta}
            data-testid="hero-cta"
            className="group bg-gold text-ink px-8 py-4 sm:px-10 sm:py-5 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:bg-gold-light hover:scale-[1.02] transition-all gold-glow rounded-sm focus:ring-2 focus:ring-gold focus:outline-none"
          >
            Vypočítať zadarmo
            <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
          </button>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="hero-phone"
            className="group border border-white/25 text-white px-8 py-4 sm:px-10 sm:py-5 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:border-gold hover:text-gold hover:bg-white/5 transition-all rounded-sm focus:ring-2 focus:ring-gold focus:outline-none"
          >
            <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
          </a>
        </div>
        <p className="text-white/60 text-sm mt-4 max-w-lg">
          Rodinný dom, bytový dom aj budova — spočítame materiál, prácu, lešenie aj dopravu.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl mt-12 md:mt-16 overflow-hidden relative z-20">
          {[
            [`${fmtM2(m2)}+ m²`, "zateplených fasád RD a BD"],
            ["60 s", "orientačný prepočet"],
            ["Overení", "partneri v každom okrese"],
            ["60 mesiacov", "záruka na dielo"],
          ].map(([n, l], i) => (
            <motion.div
              key={l}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 + i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="group flex flex-col justify-center px-6 py-6 md:p-8 border-b md:border-b-0 even:border-l lg:even:border-l-0 lg:border-r border-white/10 last:border-r-0 hover:bg-white/[0.04] transition-colors"
            >
              <div className="font-display text-2xl md:text-3xl text-white group-hover:text-gold transition-colors duration-300">{n}</div>
              <div className="mt-2 text-white/50 text-[10px] md:text-xs uppercase tracking-widest font-mono2">{l}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
